import React from 'react';
import InboxIcon from '@mui/icons-material/Inbox';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { QueueActions } from '../../services/caseWorkflowService';
import { TierPill } from '../Benchmark/BenchmarkPrimitives';
import { QUEUES_CAPTION } from './projectOutcomesVocabulary';
import { dsVar } from '../../theme/tokens';

interface ReadyQueuesProps {
    queues: QueueActions[];
    /** Opens the project's workflow that consumes a queue. */
    onOpenWorkflow: (campaignId: string) => void;
    /** Opens the gate a waiting queue is held behind. */
    onOpenGate: (gateCode: string) => void;
    gateLabels: Record<string, string>;
}

const ageText = (iso: string | null): string | null => {
    if (!iso) return null;
    const h = Math.max(0, (Date.now() - Date.parse(iso)) / 36e5);
    if (h < 48) return `${Math.round(h)} h`;
    return `${Math.round(h / 24)} days`;
};

/**
 * Work queued for the next workflow, one tile per queue.
 *
 * A queue is a claim status AND a workflow state, read from the contract, so a
 * closed report that still reads 'pending' is never counted as work. A queue
 * held behind a gate (SAR drafts waiting on EDD sign-off) links to that gate
 * rather than to a workflow, because running a workflow will not move it.
 */
const ReadyQueues: React.FC<ReadyQueuesProps> = ({ queues, onOpenWorkflow, onOpenGate, gateLabels }) => (
    <section aria-label="Ready for the next step" className="text-left">
        <div className="flex items-center gap-2 mb-2">
            <InboxIcon sx={{ fontSize: 16, color: dsVar('fg-2') }} />
            <h3 className="text-sm font-semibold text-fg m-0">Ready for the next step</h3>
            <span className="text-xs text-fg-muted truncate">{QUEUES_CAPTION}</span>
        </div>
        <div className="grid gap-3 grid-cols-2 xl:grid-cols-4">
            {queues.map((queue) => {
                const age = ageText(queue.oldestAt);
                const tiers = Object.entries(queue.byPriority).sort(([a], [b]) => b.localeCompare(a));
                return (
                    <div key={queue.code} className="rounded-xl border border-line bg-surface/40 p-3">
                        <div className="text-xs text-fg-2">{queue.label}</div>
                        <div className="flex items-baseline gap-2 mt-1">
                            <span className={`text-xl font-bold ${queue.cases ? 'text-fg' : 'text-fg-muted'}`}>
                                {queue.cases.toLocaleString()}
                            </span>
                            {queue.cases > 0 && age && <span className="text-[11px] text-fg-muted">oldest {age}</span>}
                        </div>
                        {tiers.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                                {tiers.map(([tier, n]) => (
                                    <span key={tier} className="inline-flex items-center gap-0.5">
                                        {/^P[0-9]$/.test(tier) ? <TierPill code={tier} /> : <span className="text-[11px] text-fg-2">{tier}</span>}
                                        <span className="text-[11px] text-fg-muted">×{n}</span>
                                    </span>
                                ))}
                            </div>
                        )}
                        {queue.cases > 0 && queue.awaitsGate && (
                            <button
                                type="button"
                                onClick={() => onOpenGate(queue.awaitsGate as string)}
                                className="mt-2 bg-transparent border-none p-0 cursor-pointer flex items-center gap-1 text-xs text-accent-fg/80 hover:text-accent-fg"
                            >
                                Waiting on {gateLabels[queue.awaitsGate] ?? queue.awaitsGate}
                                <ArrowForwardIcon style={{ fontSize: 13 }} />
                            </button>
                        )}
                        {queue.cases > 0 && !queue.awaitsGate && queue.campaignId && (
                            <button
                                type="button"
                                onClick={() => onOpenWorkflow(queue.campaignId as string)}
                                className="mt-2 bg-transparent border-none p-0 cursor-pointer flex items-center gap-1 text-xs text-accent-fg/80 hover:text-accent-fg text-left"
                                title={queue.campaignName ?? undefined}
                            >
                                Open {queue.campaignName ? 'workflow' : 'the workflow'}
                                <ArrowForwardIcon style={{ fontSize: 13 }} />
                            </button>
                        )}
                        {queue.cases > 0 && !queue.awaitsGate && !queue.campaignId && queue.consumerFlow && (
                            <div className="mt-2 text-[11px] text-fg-muted">
                                No workflow in this project runs this step yet.
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    </section>
);

export default ReadyQueues;
